/**
 * Редактор стилей — docs/release-2026-09-19.md §5.6. Страница `/b/:brand/styles[/:versionId]`,
 * а не вкладка шапки: сюда приходят из «Настроек», и её держат в соседней вкладке браузера,
 * пока в первой смотрят, что вышло.
 *
 * ДВЕ ПАНЕЛИ. Слева 300px — чей файл (сегмент «Писатель · Мониторинг»), какая версия (чипы,
 * «Сделать по умолчанию», «Копия → vN», «Проверить в генерации») и какой файл (список по
 * группам с точкой «изменён»). Справа — сам файл целиком во всю высоту. Ниже 1024px панели
 * встают одна под другой (docs/ui-design.md §4.1).
 *
 * РЕЛИЗ ТОЛЬКО ЧИТАЕТСЯ. Его файлы — это файлы выкладки на диске, и правит их выкладка, а не
 * интерфейс: бэкенд в `/opt/smm-agent` не пишет (§0, п. 5–7). Поэтому у релиза поле только для
 * чтения, полоса «Релиз меняется выкладкой…» и кнопка копии — единственный путь «поправить
 * релиз» здесь и есть «сделать копию и править её».
 *
 * НЕСОХРАНЁННАЯ ПРАВКА НЕ ТЕРЯЕТСЯ МОЛЧА. Переход к другому файлу, другой версии или к копии —
 * через диалог подтверждения; закрытие вкладки — через `beforeunload`. Правка живёт на ПАРЕ
 * «версия + путь», а не на отпечатке файла: перечитанный файл (сохранили в другой вкладке) не
 * смывает набранное, а превращается в 409 `style_file_stale` при сохранении — и человек сам
 * решает, перечитать ли.
 *
 * ТОТ ЖЕ ВОПРОС — У ЛЮБОГО УХОДА ВНУТРИ ПРИЛОЖЕНИЯ (ревью партии, U-B1). Ссылки шапки, полосы
 * хода, диалогов — это `<Link>` роутера, и `beforeunload` их не видит: страница не выгружается,
 * она просто снимается, и правка пропадала без слова. Пока есть несохранённая правка, нажатие
 * по ссылке своего приложения перехватывается на погружении (`inAppTarget`) и превращается в
 * тот же диалог подтверждения; «Назад»/«Вперёд» браузера — возвратом адреса и тем же вопросом.
 * А на случай ухода, который спросить нельзя (истёкшая сессия уводит на вход, перезагрузка,
 * упавшая вкладка), правка лежит копией в sessionStorage и предлагается при следующем открытии
 * того же файла с тем же отпечатком: «Восстановить» или «Отбросить».
 *
 * «ЕСТЬ ЧТО ТЕРЯТЬ» СЧИТАЕТСЯ ПО САМОЙ ПРАВКЕ (U-B5), а не по выведенному из загруженного:
 * загруженное сбрасывается вместе с ресурсом, а набранное от этого не исчезает. И ресурс не
 * сбрасывается от одного неудачного перечитывания: показывается последнее удачное с плашкой
 * «не удалось обновить» (`useLastGood`).
 *
 * ОТВЕТЫ РАЗБИРАЮТСЯ СХЕМАМИ, а не приводятся типом: чужой ответ — это слово «не загрузилось»
 * на своём месте, а не исключение посреди правки.
 */

import { codePointLen, msToIso, nowMs } from '@smm/contracts';
import {
  STYLE_AGENTS,
  STYLE_FILE_BODY_MAX,
  STYLE_FILE_GROUPS,
  STYLE_NAME_MAX,
  STYLE_NOTE_MAX,
} from '@smm/contracts/schemas';
import { type JSX, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { ApiRequestError, get, post } from '../api/client.ts';
import {
  SaveStyleFileResponseSchema,
  type StyleAgent,
  type StyleFile,
  type StyleFileGroup,
  StyleFileSchema,
  type StyleFileSummary,
  StyleFilesResponseSchema,
  type StyleVersion,
  StyleVersionSchema,
} from '../api/types.ts';
import { useLastGood } from '../api/useLastGood.ts';
import { useResource } from '../api/useResource.ts';
import { agentsOffReason, useBrands } from '../brand.tsx';
import { plural, shortMsk } from '../components/format.ts';
import { GenerationDialog } from '../components/GenerationDialog.tsx';
import { Modal } from '../components/Modal.tsx';
import { Shell } from '../components/Shell.tsx';
import { useShellSettings } from '../components/shellSettings.ts';
import { parseStyleVersions, pickableStyles, styleWithKind } from '../components/styleVersions.ts';
import styles from './StylesPage.module.css';

const AGENT_LABEL: Readonly<Record<StyleAgent, string>> = {
  writer: 'Писатель',
  monitor: 'Мониторинг',
};

/** Группы списка — порядок `STYLE_FILE_GROUPS`: ядро → голос → форматы → публикация → примеры. */
const GROUP_LABEL: Readonly<Record<StyleFileGroup, string>> = {
  core: 'Ядро',
  voice: 'Голос',
  formats: 'Форматы',
  publishing: 'Публикация',
  examples: 'Примеры',
  other: 'Прочее',
};

const errorText = (e: unknown): string =>
  e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);

/**
 * Отказ «файл уже изменили» (§5.1): 409 с причиной `style_file_stale` — кодом конверта или
 * `details.reason`, как у остальных отказов стилей (`domain/style-resolve.ts`). Прочие 409
 * (например, скрытая версия) — обычная ошибка со своим текстом, а не приглашение перечитать.
 */
function isStale(e: unknown): boolean {
  if (!(e instanceof ApiRequestError) || e.status !== 409) return false;
  if (e.code === 'style_file_stale') return true;
  const d = e.details;
  return (
    typeof d === 'object' &&
    d !== null &&
    'reason' in d &&
    (d as { reason: unknown }).reason === 'style_file_stale'
  );
}

/** Последний сегмент пути — подпись файла без заголовка (у примеров `# ` нет). */
const leafOf = (path: string): string => path.slice(path.lastIndexOf('/') + 1);

/**
 * Файл виден под вкладкой сервиса, если сервис его читает. Файл, который не читает никто
 * (нет ни в одном списке бренда), виден под обеими: иначе его нельзя было бы открыть вовсе.
 */
const readBy = (f: StyleFileSummary, agent: StyleAgent): boolean =>
  f.agents.length === 0 || f.agents.includes(agent);

/** Файл обоих сервисов — «общий»: его правка меняет и писателя, и мониторинг. */
const isShared = (agents: readonly StyleAgent[]): boolean =>
  STYLE_AGENTS.every((a) => agents.includes(a));

/**
 * Ответ копии контрактом не описан (`CloneStyleBodySchema` есть, схемы ответа нет): принимаем
 * версию целиком или `{version}`. Не разобрали — версия находится по имени в свежем списке;
 * имя уникально среди копий бренда (409 `style_name_taken`).
 */
function clonedIdOf(res: unknown): string | null {
  const direct = StyleVersionSchema.safeParse(res);
  if (direct.success) return direct.data.id;
  if (typeof res === 'object' && res !== null && 'version' in res) {
    const nested = StyleVersionSchema.safeParse((res as { version: unknown }).version);
    if (nested.success) return nested.data.id;
  }
  return null;
}

/** Набранная правка: чья она (версия + путь) и от какого файла начата. */
interface Edit {
  readonly versionId: string;
  readonly path: string;
  /** sha256 файла, который человек начал править, — `expected_sha256` сохранения. */
  readonly base: string;
  /**
   * Тело того же файла. «Есть ли что терять» считается от него, а не от загруженного сейчас:
   * загруженное сбрасывается вместе с ресурсом (сбой перечитывания, смена версии в адресе), а
   * набранное от этого не исчезает — и `beforeunload` не имеет права разоружиться (U-B5).
   */
  readonly baseBody: string;
  readonly body: string;
}

/**
 * Правка после удачного сохранения (ревью партии, U-B2). Поле не запирается на время запроса,
 * и набранное, пока он был в пути, не выбрасывается: правка продолжается поверх сохранённого
 * файла — его отпечаток и тело становятся её основой. Ничего не набрали — правки больше нет.
 * Ответ не разобран — основой становится ушедшее тело, а отпечаток следующее сохранение
 * возьмёт у перечитанного файла (`expectedSha` в `save`).
 */
function afterSave(e: Edit | null, sent: Edit, saved: StyleFile | null): Edit | null {
  if (e === null || e.versionId !== sent.versionId || e.path !== sent.path) return e;
  if (e.body === sent.body) return null;
  return saved === null
    ? { ...e, baseBody: sent.body }
    : { ...e, base: saved.sha256, baseBody: saved.body };
}

// ─────────────────────────────────────────────────────────────────────────────
// Копия несохранённой правки в sessionStorage (ревью партии, U-B1)
//
// Спросить можно не о каждом уходе: истёкшая сессия уводит на вход без вопроса, перезагрузку
// человек подтверждает в диалоге браузера, вкладка падает. Копия лежит под ключом «бренд +
// версия + путь + отпечаток основы» и предлагается, только когда тот же файл открыт с ТЕМ ЖЕ
// отпечатком: правка поверх другого содержимого — уже не та правка. Снимается сохранением и
// любым осознанным отказом («Отменить правки», «Уйти без сохранения», «Перечитать файл»,
// «Отбросить»). sessionStorage, а не localStorage: копия не выходит за вкладку и не всплывёт
// через неделю в другом окне поверх чужой работы.

const draftKey = (brand: string, e: Pick<Edit, 'versionId' | 'path' | 'base'>): string =>
  `smm.styles.draft:${brand}:${e.versionId}:${e.path}:${e.base}`;

interface StoredDraft {
  readonly body: string;
  /** Момент последней записи — ISO_UTC_MS (R11: время только через контракты). */
  readonly at: string;
}

/**
 * Хранилище — у `window`, а не у `globalThis`, и каждое обращение в try: та же форма, что у
 * хелперов `BacklogAside`/`MonitoringPage` (в Node 26 свой глобальный Web Storage перекрывает
 * jsdom; доступ бросает при запрете данных сайта, запись — при переполнении квоты). Копия —
 * страховка, а не состояние правки: без хранилища правка живёт как прежде, только без копии.
 */
function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readDraft(key: string): StoredDraft | null {
  try {
    const raw = session()?.getItem(key) ?? null;
    if (raw === null) return null;
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null) return null;
    const { body, at } = v as { readonly body?: unknown; readonly at?: unknown };
    return typeof body === 'string' && typeof at === 'string' ? { body, at } : null;
  } catch {
    return null;
  }
}

function keepDraft(key: string, body: string): void {
  try {
    session()?.setItem(key, JSON.stringify({ body, at: msToIso(nowMs()) }));
  } catch {
    /* см. session(): молчим намеренно */
  }
}

function forgetDraft(key: string): void {
  try {
    session()?.removeItem(key);
  } catch {
    /* см. session(): молчим намеренно */
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Уход со страницы внутри приложения (ревью партии, U-B1)

/**
 * Нажатие, которое снимет страницу, не выгружая её: левая кнопка без модификаторов по ссылке
 * своего источника, открывающейся в этой же вкладке. Всё прочее страницу не трогает (новая
 * вкладка, загрузка файла, чужой сайт) или выгружает целиком (`/api/…` — не маршрут
 * приложения: бэкенд отдаёт интерфейс на всё, что вне `/api/`), и тогда спрашивает
 * `beforeunload`. Возвращает адрес перехода.
 */
function inAppTarget(e: MouseEvent): URL | null {
  if (e.defaultPrevented || e.button !== 0) return null;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
  if (!(a instanceof HTMLAnchorElement) || a.hasAttribute('download')) return null;
  const target = a.getAttribute('target');
  if (target !== null && target !== '' && target !== '_self') return null;
  let url: URL;
  try {
    url = new URL(a.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || url.pathname.startsWith('/api/')) return null;
  return url;
}

/** Путь так, как его отдаёт роутер (раскодированным), — чтобы сравнивать с текущим. */
function routePath(url: URL): string {
  try {
    return decodeURI(url.pathname);
  } catch {
    return url.pathname;
  }
}

const hereInHistory = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

// ─────────────────────────────────────────────────────────────────────────────

/** «a», «a и b», «a, b и c» — перечень словами. */
function joinRu(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} и ${parts.at(-1) ?? ''}`;
}

/** Причина словами сервиса внутри своей фразы: без двух точек подряд. */
const reasonOf = (text: string): string => text.trim().replace(/\.+$/, '');

export function StylesPage({
  brand,
  versionId,
}: {
  brand: string;
  /** `null` — версия не указана: открывается стиль по умолчанию. */
  versionId: string | null;
}): JSX.Element {
  return (
    <Shell brand={brand} active={null}>
      <StylesEditor brand={brand} versionId={versionId} />
    </Shell>
  );
}

function StylesEditor({
  brand,
  versionId,
}: {
  readonly brand: string;
  readonly versionId: string | null;
}): JSX.Element {
  const [here, navigate] = useLocation();
  const settings = useShellSettings();
  const b = encodeURIComponent(brand);
  // «Проверить в генерации» ставит заявку писателю; у бренда без него кнопка гаснет с причиной,
  // как «Запустить генерацию» в шапке (docs/multi-brand.md §8). Стили при этом правятся.
  const tryOff = agentsOffReason(useBrands(), brand, ['generation']);

  // Все три ресурса страницы перечитываются в фоне (после сохранения, смены умолчания, копии)
  // и потому держат последний удачный ответ (`useLastGood`, ревью партии U-B5): один
  // неудачный GET показывает плашку «не удалось обновить», а не прячет редактор за ошибкой
  // вместе с набранной правкой. Ошибкой на месте данных остаётся только первый ответ адреса.

  // ── версии ──
  const listPath = `/api/v1/styles?brand=${b}`;
  const list = useResource<unknown>(listPath);
  const listFresh = useMemo(() => parseStyleVersions(list.data), [list.data]);
  const listShown = useLastGood(listPath, list, listFresh);
  const parsed = listShown.value;
  const { refetch: refetchList } = list;

  // Умолчание сменили в «Настройках» шапки — метка «по умолчанию» в списке могла переехать.
  const seenRev = useRef(settings.rev);
  useEffect(() => {
    if (seenRev.current === settings.rev) return;
    seenRev.current = settings.rev;
    refetchList();
  }, [settings.rev, refetchList]);

  const selectedId = versionId ?? parsed?.default_id ?? null;
  const version = parsed?.versions.find((v) => v.id === selectedId) ?? null;

  // ── файлы версии ──
  const filesPath =
    version === null ? null : `/api/v1/styles/${encodeURIComponent(version.id)}/files?brand=${b}`;
  const files = useResource<unknown>(filesPath);
  const filesFresh = useMemo(() => {
    if (files.data === undefined) return null;
    const r = StyleFilesResponseSchema.safeParse(files.data);
    return r.success ? r.data : null;
  }, [files.data]);
  const filesShown = useLastGood(filesPath, files, filesFresh);
  const filesParsed = filesShown.value;
  const filesBroken = filesParsed === null && filesShown.failure !== null;
  const allFiles = filesParsed?.files ?? [];

  const [agent, setAgent] = useState<StyleAgent>('writer');
  const [path, setPath] = useState<string | null>(null);
  // Порядок списка — группы по спецификации, внутри группы по пути; первым открывается ровно
  // тот файл, что стоит первым в списке.
  const visibleFiles = allFiles
    .filter((f) => readBy(f, agent))
    .sort(
      (x, y) =>
        STYLE_FILE_GROUPS.indexOf(x.group) - STYLE_FILE_GROUPS.indexOf(y.group) ||
        (x.path < y.path ? -1 : x.path > y.path ? 1 : 0),
    );

  // Открытый файл ЗАКРЕПЛЯЕТСЯ путём, а не выводится из фильтра: иначе переключение сегмента
  // подменило бы файл под набранной правкой. При смене версии тот же путь остаётся открытым,
  // если он в ней есть, — так версии удобно сравнивать глазами; нет — первый файл списка.
  if (filesParsed !== null && (path === null || !allFiles.some((f) => f.path === path))) {
    const first = visibleFiles[0]?.path ?? allFiles[0]?.path ?? null;
    if (first !== path) setPath(first);
  }
  const summary = allFiles.find((f) => f.path === path) ?? null;

  // ── файл ──
  const filePath =
    version === null || summary === null
      ? null
      : `/api/v1/styles/${encodeURIComponent(version.id)}/file?brand=${b}&path=${encodeURIComponent(summary.path)}`;
  const file = useResource<unknown>(filePath);
  const fileFresh = useMemo(() => {
    if (file.data === undefined) return null;
    const r = StyleFileSchema.safeParse(file.data);
    return r.success ? r.data : null;
  }, [file.data]);
  const fileShown = useLastGood(filePath, file, fileFresh);
  const fileParsed = fileShown.value;
  const fileBroken = fileParsed === null && fileShown.failure !== null;

  /**
   * Файл из ответа сохранения — до того, как перечитанный догонит его. Без него между
   * ответом POST и ответом GET поле на миг показывало бы ПРЕЖНИЙ текст. Новее тот, у кого
   * позже `updated_at`: перечитанный файл, сохранённый потом в другой вкладке, побеждает.
   */
  const [savedFile, setSavedFile] = useState<StyleFile | null>(null);
  const current: StyleFile | null =
    savedFile !== null &&
    fileParsed !== null &&
    savedFile.version_id === fileParsed.version_id &&
    savedFile.path === fileParsed.path &&
    (fileParsed.updated_at ?? '') < (savedFile.updated_at ?? '')
      ? savedFile
      : fileParsed;

  const [edit, setEdit] = useState<Edit | null>(null);
  const editing =
    edit !== null &&
    current !== null &&
    edit.versionId === current.version_id &&
    edit.path === current.path
      ? edit
      : null;
  const text = editing?.body ?? current?.body ?? '';
  // Есть что сохранять: набранное отличается от файла на экране (кнопки «Сохранить», «Отменить»).
  const dirty = editing !== null && current !== null && editing.body !== current.body;
  // Есть что терять: по самой правке, а не по выведенному из загруженного (см. `Edit`). На
  // этом стоят все охранники ухода — `beforeunload`, ссылки, «Назад», переходы по экрану.
  const unsaved = edit !== null && edit.body !== edit.baseBody;
  const tooLong = text.length > STYLE_FILE_BODY_MAX;

  // ── копия правки в хранилище ──
  // Пишется на каждое изменение правки: копия, отстающая на последние фразы, врала бы ровно
  // в тот момент, ради которого заведена. Правка без изменений копии не держит.
  useEffect(() => {
    if (edit === null) return;
    const key = draftKey(brand, edit);
    if (edit.body === edit.baseBody) forgetDraft(key);
    else keepDraft(key, edit.body);
  }, [brand, edit]);

  /** Отказ от правки — осознанный: её копия в хранилище уходит вместе с ней. */
  const dropEdit = (): void => {
    if (edit !== null) forgetDraft(draftKey(brand, edit));
    setEdit(null);
  };

  // Копия, оставшаяся от прошлого открытия ЭТОГО файла с ЭТИМ отпечатком (перезагрузка,
  // истёкшая сессия): предлагается, пока правки нет. Читается один раз на открытие — ключ
  // сменился, значит открыто другое, — а не на каждой отрисовке.
  const offerKey =
    current === null || edit !== null
      ? ''
      : draftKey(brand, {
          versionId: current.version_id,
          path: current.path,
          base: current.sha256,
        });
  const [offer, setOffer] = useState<{ readonly key: string; readonly draft: StoredDraft | null }>({
    key: '',
    draft: null,
  });
  if (offer.key !== offerKey) {
    setOffer({ key: offerKey, draft: offerKey === '' ? null : readDraft(offerKey) });
  }
  const restorable =
    offer.key === offerKey && offer.draft !== null && offer.draft.body !== current?.body
      ? offer.draft
      : null;
  const restore = (): void => {
    if (current === null || restorable === null) return;
    setEdit({
      versionId: current.version_id,
      path: current.path,
      base: current.sha256,
      baseBody: current.body,
      body: restorable.body,
    });
  };
  const discardStored = (): void => {
    forgetDraft(offerKey);
    setOffer({ key: offerKey, draft: null });
  };

  // Исходы сохранения относятся к файлу, который сохраняли: другой файл — чистый лист.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [stale, setStale] = useState(false);
  const openKey = `${version?.id ?? ''}:${path ?? ''}`;
  const [outcomeFor, setOutcomeFor] = useState(openKey);
  if (outcomeFor !== openKey) {
    setOutcomeFor(openKey);
    setSaveError(null);
    setWarnings([]);
    setStale(false);
  }
  // Ответ сохранения, пришедший после ухода к другому файлу, не рисует свой исход поверх
  // чужого: исход сверяется с тем, что открыто СЕЙЧАС, а не в момент нажатия.
  const openKeyRef = useRef(openKey);
  openKeyRef.current = openKey;

  // ── уход с несохранённой правкой ──
  const [pending, setPending] = useState<{ readonly run: () => void } | null>(null);
  // Одна функция на всю жизнь страницы: `Modal` держит фокус эффектом по `onClose`, а страница
  // перерисовывается и при открытом вопросе (ответ сохранения, перечитанный список).
  const closeConfirm = useCallback((): void => {
    setPending(null);
  }, []);
  const guard = (run: () => void): void => {
    if (unsaved) {
      setPending({ run });
      return;
    }
    // Пустая правка (набрали и стёрли) не переживает ухода с файла: вернувшись, человек нашёл
    // бы её поверх файла, который тем временем мог измениться.
    dropEdit();
    run();
  };
  useEffect(() => {
    if (!unsaved) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Старые браузеры спрашивают только при непустом returnValue.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [unsaved]);

  // Уход внутри приложения: ссылки роутера и «Назад»/«Вперёд» браузера (U-B1). Вопрос —
  // тот же диалог, что у перехода к другому файлу; согласие — переход роутером («~» — адрес
  // целиком, мимо базы роутера: он взят у ссылки, где база уже учтена).
  useEffect(() => {
    if (!unsaved) return undefined;
    const ask = (to: string): void => {
      setPending({
        run: () => {
          navigate(`~${to}`);
        },
      });
    };
    // На погружении и на `document`: раньше обработчика роутера у ссылки (он у корня React),
    // поэтому `preventDefault` здесь — и `<Link>` видит `defaultPrevented` и не переходит.
    // Распространение не глушится: свой `onClick` ссылки (закрыть диалог, из которого ушли)
    // отрабатывает как обычно, и вопрос встаёт на экране один, а не поверх другого диалога.
    const onClick = (e: MouseEvent): void => {
      const url = inAppTarget(e);
      // Ссылка на эту же страницу («Редактор стилей →» из «Настроек» на нём же) ничего не снимает.
      if (url === null || routePath(url) === here) return;
      e.preventDefault();
      ask(`${url.pathname}${url.search}${url.hash}`);
    };
    // «Назад»/«Вперёд»: история сдвигается раньше, чем об этом узнаёт страница, — роутер
    // перерисуется только микрозадачей, после этого обработчика. Адрес возвращается на место
    // той же записью, и страница не снимается вовсе; дальше — тот же вопрос.
    const stay = hereInHistory();
    const onPopState = (): void => {
      const to = hereInHistory();
      if (to === stay) return;
      window.history.pushState(null, '', stay);
      ask(to);
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [unsaved, navigate, here]);

  // ── действия с версией ──
  const [cloning, setCloning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [versionBusy, setVersionBusy] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);

  const openVersion = (id: string): void => {
    if (id === version?.id) return;
    guard(() => {
      setVersionError(null);
      navigate(`/b/${b}/styles/${encodeURIComponent(id)}`);
    });
  };

  const makeDefault = (v: StyleVersion): void => {
    setVersionBusy(true);
    setVersionError(null);
    // Релиз — это `null`: выбор релиза снимает выбор, а не закрепляет строку релиза (§5.1).
    post('/api/v1/style-default', {
      brand,
      version_id: v.kind === 'release' ? null : v.id,
      note: null,
    })
      .then(() => {
        setVersionBusy(false);
        refetchList();
        // Шапка подписана стилем по умолчанию — она перечитывает его сама.
        settings.changed();
      })
      .catch((e: unknown) => {
        setVersionBusy(false);
        setVersionError(`Умолчание не сменилось: ${errorText(e)}`);
      });
  };

  const save = (): void => {
    if (version === null || current === null || editing === null) return;
    const savedKey = openKey;
    const sent = editing;
    // Отпечаток, на который ложится правка. Файл адресован содержимым (sha256 тела), поэтому
    // файл на экране с тем же телом, от которого начата правка, — ровно её основа, какой бы
    // запрос его ни принёс; иначе — отпечаток начала правки, и сервер честно ответит 409.
    const expectedSha = current.body === sent.baseBody ? current.sha256 : sent.base;
    setSaving(true);
    setSaveError(null);
    setWarnings([]);
    setStale(false);
    post<unknown>(`/api/v1/styles/${encodeURIComponent(version.id)}/file`, {
      brand,
      path: current.path,
      body: sent.body,
      expected_sha256: expectedSha,
    })
      .then((res) => {
        setSaving(false);
        // Точка «изменён» у файла, отпечаток и время версии — перечитываются в любом случае.
        files.refetch();
        refetchList();
        // Сохранённое больше не «несохранённое»: копия под прежним отпечатком снята, а правка,
        // продолженная во время запроса, запишется копией под новым.
        forgetDraft(draftKey(brand, sent));
        if (openKeyRef.current !== savedKey) return;
        const r = SaveStyleFileResponseSchema.safeParse(res);
        if (r.success) {
          setSavedFile(r.data.file);
          setWarnings(r.data.warnings);
        }
        const saved = r.success ? r.data.file : null;
        setEdit((e) => afterSave(e, sent, saved));
        file.refetch();
      })
      .catch((e: unknown) => {
        setSaving(false);
        if (openKeyRef.current !== savedKey) return;
        if (isStale(e)) {
          setStale(true);
          return;
        }
        setSaveError(errorText(e));
      });
  };

  const reread = (): void => {
    setStale(false);
    dropEdit();
    setSavedFile(null);
    file.refetch();
  };

  // ── перечитывание не удалось: показано прежнее (U-B5) ──
  // Только то, что НА ЭКРАНЕ прежнее: ресурс без единого удачного ответа — это ошибка на месте
  // данных, и в плашку он не входит.
  const staleParts = [
    { what: 'список версий', value: parsed, why: listShown.failure, retry: refetchList },
    { what: 'файлы версии', value: filesParsed, why: filesShown.failure, retry: files.refetch },
    { what: 'открытый файл', value: fileParsed, why: fileShown.failure, retry: file.refetch },
  ].filter((p) => p.value !== null && p.why !== null);
  const retryStale = (): void => {
    for (const p of staleParts) p.retry();
  };

  // ── раскладка ──
  // Вопрос об уходе рисуется в ЛЮБОЙ ветке: охранники ссылок и «Назад» срабатывают по самой
  // правке, а она переживает и смену версии в адресе, и ветку «такой версии нет». Перехваченная
  // ссылка без видимого вопроса была бы просто мёртвой ссылкой.
  const confirm =
    pending === null ? null : (
      <Modal title="Несохранённые правки" width={460} onClose={closeConfirm}>
        <span>{`В файле ${edit?.path ?? current?.path ?? ''} есть несохранённые правки. Если уйти, они пропадут.`}</span>
        <div className={styles['actions']}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={closeConfirm}>
            Остаться
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              const { run } = pending;
              setPending(null);
              dropEdit();
              run();
            }}
          >
            Уйти без сохранения
          </button>
        </div>
      </Modal>
    );

  if (parsed === null) {
    return (
      <div className={styles['page']}>
        <h1 className="h1">Редактор стилей</h1>
        {listShown.failure === null ? (
          <span className="micro">Загружаем версии стиля…</span>
        ) : (
          <div className={`val val-err ${styles['notice']}`} role="alert">
            <span className={styles['grow']}>
              {`Версии стиля не загрузились: ${reasonOf(listShown.failure)}.`}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={refetchList}>
              Повторить
            </button>
          </div>
        )}
        {confirm}
      </div>
    );
  }
  if (version === null) {
    return (
      <div className={styles['page']}>
        <h1 className="h1">Редактор стилей</h1>
        <div className="val val-warn" role="alert">
          <span>Такой версии стиля у бренда нет.</span>
        </div>
        <Link href={`/b/${b}/styles`} className="btn btn-ghost btn-sm">
          Открыть стиль по умолчанию
        </Link>
        {confirm}
      </div>
    );
  }

  const chips = pickableStyles(parsed.versions, version.id);
  const editable = current?.editable ?? version.kind === 'custom';
  const cloneLabel = `Копия → ${parsed.next_name}`;
  const chars = codePointLen(text);

  const versionMeta = [
    version.kind === 'release'
      ? 'файлы выкладки'
      : `копия ${version.base_name ?? '—'}${version.created_by_login === null ? '' : ` · ${version.created_by_login}`}`,
    `${String(version.files_count)} ${plural(version.files_count, 'файл', 'файла', 'файлов')}`,
    `изменена ${shortMsk(version.updated_at)}`,
    ...(version.note === null ? [] : [version.note]),
  ].join(' · ');

  return (
    <div className={styles['page']}>
      <div className={styles['head']}>
        <h1 className="h1">Редактор стилей</h1>
        <span className="meta">{`${styleWithKind(version)} · ${versionMeta}`}</span>
      </div>

      {staleParts.length === 0 ? null : (
        <div className={`val val-warn ${styles['notice']}`} role="status">
          <span className={styles['grow']}>
            {`Не удалось обновить ${joinRu(staleParts.map((p) => p.what))}: ${reasonOf(staleParts[0]?.why ?? '')}. На экране — последнее загруженное.`}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={retryStale}>
            Повторить
          </button>
        </div>
      )}

      <div className={styles['panes']}>
        <aside className={styles['left']} aria-label="Версии и файлы стиля">
          {/* fieldset, а не div с role="group": группа переключателей — ровно тот случай, ради
              которого элемент существует. Рамку и поля браузера снимает модуль. */}
          <fieldset className={`seg ${styles['seg']}`} aria-label="Чьи файлы">
            {STYLE_AGENTS.map((a) => (
              <button
                key={a}
                type="button"
                className={`${styles['segButton']} seg-o${agent === a ? ' on' : ''}`}
                aria-pressed={agent === a}
                onClick={() => {
                  setAgent(a);
                }}
              >
                {AGENT_LABEL[a]}
              </button>
            ))}
          </fieldset>

          <div className={styles['block']}>
            <fieldset className={styles['versions']}>
              <legend className="lbl">Версия</legend>
              <div className={styles['chips']}>
                {chips.map((v) => {
                  const on = v.id === version.id;
                  const marks = [
                    ...(v.kind === 'release' && v.name !== 'релиз' ? ['релиз'] : []),
                    ...(v.id === parsed.default_id ? ['по умолчанию'] : []),
                    ...(v.archived ? ['скрыта'] : []),
                  ];
                  return (
                    <button
                      key={v.id}
                      type="button"
                      className={on ? 'fchip on' : 'fchip'}
                      aria-pressed={on}
                      onClick={() => {
                        openVersion(v.id);
                      }}
                    >
                      {marks.length === 0 ? v.name : `${v.name} (${marks.join(', ')})`}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <div className={styles['versionActions']}>
              {version.is_default || version.archived ? null : (
                <button
                  type="button"
                  className={versionBusy ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'}
                  disabled={versionBusy}
                  onClick={() => {
                    makeDefault(version);
                  }}
                >
                  Сделать по умолчанию
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  guard(() => {
                    setCloning(true);
                  });
                }}
              >
                {cloneLabel}
              </button>
              <button
                type="button"
                className={
                  tryOff !== null || unsaved || version.archived
                    ? 'btn btn-sm btn-dis'
                    : 'btn btn-ghost btn-sm'
                }
                disabled={tryOff !== null || unsaved || version.archived}
                title={
                  tryOff !== null
                    ? `${tryOff.charAt(0).toUpperCase()}${tryOff.slice(1)}`
                    : unsaved
                      ? 'Сначала сохраните правки — генерация пишет сохранённой версией'
                      : version.archived
                        ? 'Скрытой версией писать нельзя'
                        : undefined
                }
                onClick={() => {
                  setTesting(true);
                }}
              >
                Проверить в генерации
              </button>
            </div>
            {versionError === null ? null : (
              <div className="val val-err" role="alert">
                <span>{versionError}</span>
              </div>
            )}
          </div>

          <nav className={styles['files']} aria-label="Файлы стиля">
            {filesBroken ? (
              <div className={`val val-err ${styles['notice']}`} role="alert">
                <span className={styles['grow']}>
                  {`Файлы версии не загрузились: ${reasonOf(filesShown.failure ?? '')}.`}
                </span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={files.refetch}>
                  Повторить
                </button>
              </div>
            ) : filesParsed === null ? (
              <span className="micro">Загружаем файлы…</span>
            ) : visibleFiles.length === 0 ? (
              <span className="micro">Файлов этого сервиса в версии нет.</span>
            ) : (
              STYLE_FILE_GROUPS.map((g) => {
                const inGroup = visibleFiles.filter((f) => f.group === g);
                if (inGroup.length === 0) return null;
                return (
                  <div key={g} className={styles['group']}>
                    <span className="eyebrow">{GROUP_LABEL[g]}</span>
                    {inGroup.map((f) => {
                      const on = f.path === path;
                      return (
                        <button
                          key={f.path}
                          type="button"
                          className={on ? `${styles['file']} ${styles['fileOn']}` : styles['file']}
                          aria-current={on ? 'true' : undefined}
                          onClick={() => {
                            if (f.path !== path) {
                              guard(() => {
                                setPath(f.path);
                              });
                            }
                          }}
                        >
                          <span className={styles['fileName']}>{f.title ?? leafOf(f.path)}</span>
                          <span className={styles['fileMeta']}>
                            <span className={styles['filePath']}>{f.path}</span>
                            {isShared(f.agents) ? (
                              <span className={styles['badge']}>общий</span>
                            ) : null}
                            {f.changed ? (
                              <span
                                className={styles['dot']}
                                role="img"
                                aria-label="изменён"
                                title={`изменён относительно ${version.base_name ?? 'базовой версии'}`}
                              />
                            ) : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </nav>
        </aside>

        <section className={styles['right']} aria-label="Файл стиля">
          {version.kind === 'release' ? (
            <div className={`notice ${styles['banner']}`}>
              <span>Релиз меняется выкладкой — сделайте копию, чтобы править здесь</span>
              <button
                type="button"
                className="btn btn-pri btn-sm"
                onClick={() => {
                  guard(() => {
                    setCloning(true);
                  });
                }}
              >
                {cloneLabel}
              </button>
            </div>
          ) : null}

          {summary === null ? (
            filesParsed === null ? null : (
              <span className="micro">Выберите файл слева.</span>
            )
          ) : fileBroken ? (
            <div className={`val val-err ${styles['notice']}`} role="alert">
              <span className={styles['grow']}>
                {`Файл не открылся: ${reasonOf(fileShown.failure ?? '')}.`}
              </span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={file.refetch}>
                Повторить
              </button>
            </div>
          ) : current === null ? (
            <span className="micro">Загружаем файл…</span>
          ) : (
            <>
              <div className={styles['fileHead']}>
                <h2 className="h2">{summary.title ?? leafOf(summary.path)}</h2>
                <div className={styles['fileHeadMeta']}>
                  <span className="meta">{current.path}</span>
                  {isShared(summary.agents) ? <span className={styles['badge']}>общий</span> : null}
                  {current.updated_by_login === null || current.updated_at === null ? null : (
                    <span className="meta">
                      {`${current.updated_by_login} · ${shortMsk(current.updated_at)}`}
                    </span>
                  )}
                </div>
              </div>

              {restorable === null ? null : (
                <div className={`val val-warn ${styles['notice']}`} role="status">
                  <span className={styles['grow']}>
                    {`Есть несохранённая правка от ${shortMsk(restorable.at)}`}
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={restore}>
                    Восстановить
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={discardStored}>
                    Отбросить
                  </button>
                </div>
              )}

              {/* Пока копия не восстановлена и не отброшена, поле только читается: первая же
                  буква начала бы новую правку, и её копия легла бы под тот же ключ — поверх
                  той, что предлагают восстановить. */}
              <textarea
                className={`inp ${styles['area']}`}
                aria-label={`Текст файла ${current.path}`}
                value={text}
                readOnly={!editable || restorable !== null}
                onChange={(e) => {
                  if (!editable || restorable !== null) return;
                  setEdit({
                    versionId: current.version_id,
                    path: current.path,
                    base: editing?.base ?? current.sha256,
                    baseBody: editing?.baseBody ?? current.body,
                    body: e.target.value,
                  });
                }}
              />

              {tooLong ? (
                <div className="val val-err" role="alert">
                  <span>{`Файл длиннее ${String(STYLE_FILE_BODY_MAX)} знаков — столько сервер не примет.`}</span>
                </div>
              ) : null}
              {stale ? (
                <div className={`val val-warn ${styles['notice']}`} role="alert">
                  <span>
                    Файл уже изменили — в другой вкладке или другой человек. Перечитайте его: ваши
                    правки при этом пропадут, скопируйте их, если нужны.
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={reread}>
                    Перечитать файл
                  </button>
                </div>
              ) : null}
              {saveError === null ? null : (
                <div className="val val-err" role="alert">
                  <span className={styles['preLine']}>{saveError}</span>
                </div>
              )}
              {warnings.length === 0 ? null : (
                <div className="val val-warn" role="status">
                  <span className={styles['lines']}>
                    {warnings.map((w) => (
                      <span key={w}>{w}</span>
                    ))}
                  </span>
                </div>
              )}

              {editable ? (
                <div className={styles['fileBar']}>
                  <button
                    type="button"
                    className={
                      !dirty || saving || tooLong ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'
                    }
                    disabled={!dirty || saving || tooLong}
                    onClick={save}
                  >
                    Сохранить
                  </button>
                  <button
                    type="button"
                    className={!dirty || saving ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'}
                    disabled={!dirty || saving}
                    onClick={dropEdit}
                  >
                    Отменить правки
                  </button>
                  <span className={styles['counter']} title="в кодпоинтах, файл целиком">
                    {`${String(chars)} ${plural(chars, 'знак', 'знака', 'знаков')}`}
                  </span>
                </div>
              ) : (
                <div className={styles['fileBar']}>
                  <span className={styles['counter']} title="в кодпоинтах, файл целиком">
                    {`${String(chars)} ${plural(chars, 'знак', 'знака', 'знаков')}`}
                  </span>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {confirm}

      {cloning ? (
        <CloneDialog
          brand={brand}
          base={version}
          suggested={parsed.next_name}
          onClose={() => {
            setCloning(false);
          }}
          onCloned={(id) => {
            setCloning(false);
            refetchList();
            if (id !== null) navigate(`/b/${b}/styles/${encodeURIComponent(id)}`);
          }}
        />
      ) : null}

      {testing ? (
        <GenerationDialog
          brand={brand}
          initialStyleId={version.id}
          onClose={() => {
            setTesting(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * «Копия → vN»: имя предложено сервером (`next_name` — «v» и число на единицу больше
 * наибольшего), но это предложение, а не правило. Заметка необязательна — она объясняет, ЗАЧЕМ
 * копия, и видна в мете версии.
 */
function CloneDialog({
  brand,
  base,
  suggested,
  onClose,
  onCloned,
}: {
  readonly brand: string;
  readonly base: StyleVersion;
  readonly suggested: string;
  readonly onClose: () => void;
  /** `null` — копия создана, но её id из ответа не извлечь: список перечитается и так. */
  readonly onCloned: (id: string | null) => void;
}): JSX.Element {
  const nameId = useId();
  const noteId = useId();
  const [name, setName] = useState(suggested);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typed = name.trim();
  // Имя — одна строка без управляющих символов (StyleNameSchema); пробелы по краям срезаются
  // здесь, а не отвергаются: человек их не видит и исправлять их не должен.
  const nameIssue =
    typed === ''
      ? 'Имя не может быть пустым.'
      : /[\r\n\t]/.test(typed)
        ? 'Имя — одна строка.'
        : null;
  const blocked = busy || nameIssue !== null;

  const submit = (): void => {
    setBusy(true);
    setError(null);
    post<unknown>('/api/v1/styles/clone', {
      brand,
      base_id: base.id,
      name: typed,
      note: note.trim() === '' ? null : note.trim(),
    }).then(
      (res) => {
        const direct = clonedIdOf(res);
        // Копия УЖЕ создана: отказ поиска по имени — не ошибка копии, а «не знаю, куда
        // перейти». Тогда диалог закрывается, список перечитывается, и копия видна в нём.
        const found =
          direct !== null
            ? Promise.resolve(direct)
            : get<unknown>(`/api/v1/styles?brand=${encodeURIComponent(brand)}`).then(
                (fresh) =>
                  parseStyleVersions(fresh)?.versions.find(
                    (v) => v.kind === 'custom' && v.name === typed,
                  )?.id ?? null,
                () => null,
              );
        return found.then((id) => {
          setBusy(false);
          onCloned(id);
        });
      },
      (e: unknown) => {
        setBusy(false);
        setError(errorText(e));
      },
    );
  };

  return (
    <Modal title="Копия стиля" subtitle={`из ${styleWithKind(base)}`} width={480} onClose={onClose}>
      <div>
        <label className="lbl" htmlFor={nameId}>
          Имя
        </label>
        <input
          id={nameId}
          className="inp"
          value={name}
          maxLength={STYLE_NAME_MAX}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        {nameIssue === null ? null : <span className="val val-err">{nameIssue}</span>}
      </div>
      <div>
        <label className="lbl" htmlFor={noteId}>
          Заметка
        </label>
        <input
          id={noteId}
          className="inp"
          value={note}
          maxLength={STYLE_NOTE_MAX}
          placeholder="что меняем в этой копии"
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </div>
      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}
      <div className={styles['actions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className={blocked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={blocked}
          onClick={submit}
        >
          Сделать копию
        </button>
      </div>
    </Modal>
  );
}
